import { useRef, useEffect, useState } from "react";
import api from "@/lib/api";
import ImagePicker from "./ImagePicker";
import { toast } from "sonner";
import { promptDialog } from "@/lib/dialogs";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Link2, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, PenTool, Plus, Minus } from "lucide-react";

export const PLACEHOLDERS = [
  ["trading_name", "Client name"],
  ["client.preferred_name", "Preferred name"],
  ["legal_name", "Legal name"],
  ["company_number", "Company number"],
  ["entity_type", "Entity type"],
  ["year_end", "Year end"],
  ["cs01_date", "CS01 date"],
  ["client.contact_name", "Contact name"],
  ["client.email", "Contact email"],
  ["client.phone", "Contact phone"],
  ["client.vat_number", "VAT number"],
  ["client.address", "Registered address"],
  ["client.industry", "Industry"],
  ["proposal_number", "Proposal number"],
  ["issue_date", "Issue date"],
  ["expiry_date", "Expiry date"],
  ["effective_date", "Effective date"],
  ["owner_name", "Prepared by"],
  ["net_total", "Net total"],
  ["gross_total", "Gross total"],
];

const Tool = ({ onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="w-8 h-8 grid place-items-center rounded text-cass-slate hover:bg-cass-bone hover:text-cass-navy transition-colors"
    data-testid={`rt-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    {children}
  </button>
);

export default function RichText({ value, onChange, testId = "richtext-editor" }) {
  const ref = useRef(null);
  const [pick, setPick] = useState(false);
  const selectedImg = useRef(null);
  const rootRef = useRef(null);
  const [selBox, setSelBox] = useState(null);
  const [selCap, setSelCap] = useState("");
  const syncBox = () => {
    const img = selectedImg.current, root = rootRef.current;
    if (!img || !root) { setSelBox(null); return; }
    const r = img.getBoundingClientRect(), rr = root.getBoundingClientRect();
    setSelBox({ left: r.left - rr.left, top: r.top - rr.top, width: r.width, height: r.height });
  };
  const selectImg = (img) => { selectedImg.current = img; setTimeout(syncBox, 0); };
  const startResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    const img = selectedImg.current; if (!img) return;
    const parentW = img.parentElement?.clientWidth || img.getBoundingClientRect().width;
    const startX = e.clientX, startW = img.getBoundingClientRect().width;
    const onMove = (ev) => {
      let px = Math.round(startW + (ev.clientX - startX));
      px = Math.max(40, Math.min(px, parentW));
      img.setAttribute("data-width", px + "px"); applyImg(img); syncBox();
    };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); emit(); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };
  useEffect(() => {
    const on = () => { if (selectedImg.current) syncBox(); };
    window.addEventListener("resize", on, true); window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on, true); window.removeEventListener("scroll", on, true); };
  }, []);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
      selectedImg.current = null; setSelBox(null); setSelCap("");
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");
  const exec = (cmd, arg) => { ref.current?.focus(); document.execCommand(cmd, false, arg); emit(); };
  const setFont = (css) => {
    if (!css) return;
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("fontName", false, css);
    emit();
  };
  const insertField = (token) => {
    if (!token) return;
    ref.current?.focus();
    document.execCommand("insertText", false, `{{${token}}}`);
    emit();
  };
  const insertImage = (path, url) => {
    // Store a host-relative src so embedded images survive a domain/deploy change.
    const src = path ? `/api/public/files/${path}` : url;
    ref.current?.focus();
    document.execCommand("insertHTML", false, `<img src="${src}" style="max-width:100%;height:auto;" /><p><br/></p>`);
    emit();
  };
  const applyImg = (img) => {
    const align = img.getAttribute("data-align") || "center";
    const w = img.getAttribute("data-width") || "";
    let css = "height:auto;max-width:100%;";
    if (w) css += `width:${w};`;
    if (align === "left") css += "float:left;margin:0 14px 8px 0;";
    else if (align === "right") css += "float:right;margin:0 0 8px 14px;";
    else css += "display:block;margin:10px auto;float:none;";
    img.style.cssText = css;
  };
  const alignImg = (mode) => {
    const img = selectedImg.current;
    if (!img) { toast.error("Click an image in the text first, then choose an alignment"); return; }
    img.setAttribute("data-align", mode);
    applyImg(img); emit();
  };
  const setImgWidth = (w) => {
    const img = selectedImg.current;
    if (!img) { toast.error("Click an image in the text first, then set its size"); return; }
    img.setAttribute("data-width", w); applyImg(img); emit();
  };
  const nudgeImg = (delta) => {
    const img = selectedImg.current;
    if (!img) { toast.error("Click an image in the text first, then resize it"); return; }
    const cur = parseInt(img.getAttribute("data-width"), 10) || Math.round(img.getBoundingClientRect().width);
    const maxW = img.parentElement?.clientWidth || cur;
    const next = Math.max(40, Math.min(maxW, cur + delta));
    img.setAttribute("data-width", next + "px"); applyImg(img); emit();
  };
  const insertSignature = async () => {
    try {
      const { data } = await api.get("/signature");
      if (!data?.path) { toast.error("No firm signature saved yet — set it in Admin → Engagement (LoE) → Notifications"); return; }
      ref.current?.focus();
      document.execCommand("insertHTML", false, `<img src="/api/public/files/${data.path}" data-align="right" style="width:220px;height:auto;float:right;margin:0 0 8px 14px;" /><p><br/></p>`);
      emit();
    } catch { toast.error("Could not load the saved signature"); }
  };

  return (
    <div ref={rootRef} className="relative border border-border rounded-md bg-white">
      <div className="flex items-center gap-0.5 flex-wrap border-b border-border px-1.5 py-1">
        <Tool title="Bold" onClick={() => exec("bold")}><Bold className="w-4 h-4" /></Tool>
        <Tool title="Italic" onClick={() => exec("italic")}><Italic className="w-4 h-4" /></Tool>
        <Tool title="Underline" onClick={() => exec("underline")}><Underline className="w-4 h-4" /></Tool>
        <span className="w-px h-5 bg-border mx-1" />
        <Tool title="Heading" onClick={() => exec("formatBlock", "H2")}><Heading2 className="w-4 h-4" /></Tool>
        <Tool title="Subheading" onClick={() => exec("formatBlock", "H3")}><Heading3 className="w-4 h-4" /></Tool>
        <span className="w-px h-5 bg-border mx-1" />
        <Tool title="Bullet list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></Tool>
        <Tool title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" /></Tool>
        <Tool title="Link" onClick={async () => { const u = await promptDialog({ title: "Insert link", label: "Link URL", placeholder: "https://…", confirmText: "Insert" }); if (u) exec("createLink", u); }}><Link2 className="w-4 h-4" /></Tool>
        <Tool title="Image" onClick={() => setPick(true)}><ImageIcon className="w-4 h-4" /></Tool>
        <Tool title="Align image left" onClick={() => alignImg("left")}><AlignLeft className="w-4 h-4" /></Tool>
        <Tool title="Centre image" onClick={() => alignImg("center")}><AlignCenter className="w-4 h-4" /></Tool>
        <Tool title="Align image right" onClick={() => alignImg("right")}><AlignRight className="w-4 h-4" /></Tool>
        <span className="w-px h-5 bg-border mx-1" />
        <Tool title="Shrink image" onClick={() => nudgeImg(-40)}><Minus className="w-4 h-4" /></Tool>
        <Tool title="Enlarge image" onClick={() => nudgeImg(40)}><Plus className="w-4 h-4" /></Tool>
        <select onChange={(e) => { if (e.target.value) setImgWidth(e.target.value); e.target.value = ""; }} defaultValue="" className="text-xs border border-border rounded px-1 py-1 text-cass-slate bg-white" data-testid="rt-image-width" title="Lock image size">
          <option value="" disabled>Size…</option>
          <option value="150px">Small (150px)</option>
          <option value="300px">Medium (300px)</option>
          <option value="450px">Large (450px)</option>
          <option value="600px">X-Large (600px)</option>
          <option value="100%">Full width</option>
        </select>
        <Tool title="Insert saved signature" onClick={insertSignature}><PenTool className="w-4 h-4" /></Tool>
        <span className="w-px h-5 bg-border mx-1" />
        <select onChange={(e) => { setFont(e.target.value); e.target.value = ""; }} defaultValue="" className="text-xs border border-border rounded px-1 py-1 text-cass-slate bg-white" data-testid="rt-font" title="Font">
          <option value="" disabled>Font…</option>
          <option value="Arial, Helvetica, sans-serif">Sans</option>
          <option value="Georgia, 'Times New Roman', serif">Serif</option>
          <option value="'Courier New', monospace">Mono</option>
        </select>
        <span className="w-px h-5 bg-border mx-1" />
        <select
          onChange={(e) => { insertField(e.target.value); e.target.value = ""; }}
          className="text-xs border border-border rounded px-2 py-1 text-cass-slate bg-white"
          data-testid="rt-insert-field"
          defaultValue=""
        >
          <option value="" disabled>Insert field…</option>
          {PLACEHOLDERS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onClick={(e) => { if (e.target?.tagName === "IMG") selectImg(e.target); else { selectedImg.current = null; setSelBox(null); } }}
        className="rt-content min-h-[120px] px-3 py-2 text-sm text-cass-slate leading-relaxed focus:outline-none"
        data-testid={testId}
      />
      {selBox && (
        <>
          <div style={{ position: "absolute", left: selBox.left, top: selBox.top, width: selBox.width, height: selBox.height, border: "1px solid #2EA3FD", pointerEvents: "none", boxSizing: "border-box" }} />
          <div onMouseDown={startResize} data-testid="rt-image-resize-handle" title="Drag to resize"
            style={{ position: "absolute", left: selBox.left + selBox.width - 7, top: selBox.top + selBox.height - 7, width: 14, height: 14, background: "#2EA3FD", border: "2px solid #fff", borderRadius: 3, cursor: "nwse-resize", zIndex: 5 }} />
          <input value={selCap} onChange={(e) => { setSelCap(e.target.value); if (selectedImg.current) { selectedImg.current.setAttribute("data-caption", e.target.value); emit(); } }}
            placeholder="Add a caption…" data-testid="rt-image-caption"
            style={{ position: "absolute", left: selBox.left, top: selBox.top + selBox.height + 4, width: Math.max(140, selBox.width), fontSize: 11, fontStyle: "italic", color: "#475569", border: "1px dashed #cbd5e1", borderRadius: 4, padding: "3px 8px", background: "#fff", zIndex: 6 }} />
        </>
      )}
      <ImagePicker open={pick} onOpenChange={setPick} onSelect={insertImage} />
    </div>
  );
}
